import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@tempo-io/sui-breadcrumb';
import React from 'react';
import type { Item, ItemAncestor } from '@/common/api/types/items';
import { NativeItemIndicator } from '@/common/components/icons/NativeItemIndicator';
import { OverflowTooltip } from '@/common/components/ui/OverflowTooltip';
import { useItemAncestorsQuery } from '@/common/queries/useGetItemAncestorsQuery';
import { sortAncestorsByDepth } from '@/common/utils/sortAncestorsByDepth';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';

interface BreadcrumbAncestorsProps {
  item: Item;
  enabled?: boolean;
  staticAncestors?: readonly ItemAncestor[];
  disableNavigation?: boolean;
}

export const BreadcrumbAncestors = ({
  item,
  enabled = true,
  staticAncestors,
  disableNavigation = false,
}: BreadcrumbAncestorsProps) => {
  const { data: fetchedAncestors = [], isLoading } = useItemAncestorsQuery({
    itemId: item.id,
    enabled: enabled && staticAncestors === undefined,
  });
  const { openItemPanel } = useItemPanelNavigation();

  const ancestors = staticAncestors ?? fetchedAncestors;
  const sortedAncestors = sortAncestorsByDepth(ancestors as ItemAncestor[]);
  const isHidden = !isLoading && sortedAncestors.length === 0;

  return (
    <div className={isHidden ? 'invisible' : undefined} data-testid="breadcrumb-ancestors">
      <Breadcrumb>
        <BreadcrumbList className="!flex-nowrap">
          {sortedAncestors.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 min-w-0 shrink overflow-hidden max-w-[40%]">
                {sortedAncestors.map((ancestor: ItemAncestor, idx: number) => (
                  <React.Fragment key={ancestor.id}>
                    <BreadcrumbItem className="min-w-0 shrink">
                      {disableNavigation ? (
                        <BreadcrumbPage className="block min-w-0 overflow-hidden">
                          <OverflowTooltip>{ancestor.name}</OverflowTooltip>
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink
                          className="cursor-pointer block min-w-0 overflow-hidden"
                          onClick={() => openItemPanel({ itemId: ancestor.id })}
                        >
                          <OverflowTooltip>{ancestor.name}</OverflowTooltip>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {idx < sortedAncestors.length - 1 && (
                      <BreadcrumbSeparator className="shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <BreadcrumbSeparator className="shrink-0" />
            </>
          )}
          <BreadcrumbItem className="min-w-0 flex-1">
            <BreadcrumbPage className="flex items-center gap-1">
              <OverflowTooltip className="min-w-0 flex-1">{item.name}</OverflowTooltip>
              <NativeItemIndicator externalKey={item.externalKey} />
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default BreadcrumbAncestors;
